import type { ReactNode } from "react";
import { createContext, useContext } from "react";

interface SecondarySidebarContextValue {
    /** The secondary sidebar content to render */
    secondarySidebar: ReactNode | null;
    /** Whether to force the main sidebar into collapsed state */
    forceCollapsed: boolean;
}

const SecondarySidebarContext = createContext<SecondarySidebarContextValue>({
    secondarySidebar: null,
    forceCollapsed: false,
});

interface SecondarySidebarProviderProps {
    children: ReactNode;
    /** The secondary sidebar content to render */
    secondarySidebar: ReactNode;
    /** Whether to force the main sidebar into collapsed state. Defaults to true when secondarySidebar is provided. */
    forceCollapsed?: boolean;
}

/**
 * AIDEV-NOTE: Provider for secondary sidebar configuration.
 * Wrap route layouts with this to provide a secondary navigation sidebar
 * that appears between the main sidebar and content area.
 *
 * The Layout component (ResponsiveLayout) will consume this context
 * and pass the values to DesktopLayout automatically.
 */
export function SecondarySidebarProvider({
    children,
    secondarySidebar,
    forceCollapsed = true,
}: SecondarySidebarProviderProps) {
    return (
        <SecondarySidebarContext.Provider
            value={{ secondarySidebar, forceCollapsed }}
        >
            {children}
        </SecondarySidebarContext.Provider>
    );
}

/**
 * Hook to access secondary sidebar context.
 * Returns null sidebar and false forceCollapsed if not within a provider.
 */
export function useSecondarySidebar() {
    return useContext(SecondarySidebarContext);
}
